/**
 * @file capture/gainStage.ts
 * @brief Makeup gain + peak limiter between the mic and MediaRecorder.
 *
 * @details
 * The web boardroom captures with the raw profile (`captureProfile.ts`) — no AGC.
 * That is correct for diarization (natural per-speaker levels survive) but it
 * removes the only thing lifting a distant talker, and unlike native the browser
 * had no gain stage to put it back. Native answers this in Table Mode with a 2.5x
 * boost plus a soft-knee limiter; this is the browser equivalent, with two
 * deliberate differences from the native numbers:
 *
 * 1. **Gain is 2.0, not native's 2.5.** Table Mode is an OPT-IN far-field mode —
 *    the user turns it on when the phone is in the middle of a table. The browser
 *    stage has no such toggle and runs for everyone, including someone sitting a
 *    foot from their laptop, so it is scoped for the always-on case. This value is
 *    a judgement call, NOT field-tested; see the caveat below.
 * 2. **Limiting is a `DynamicsCompressorNode`, not a waveshaper.** Porting native's
 *    per-sample knee (|x| > 0.95 compressed 0.15x) directly is a trap at these
 *    gains: with 2.5x it starts limiting at an input of 0.38, so every sample from
 *    0.38 to 1.0 lands inside the output band 0.95–0.999. That is brick-wall
 *    clipping wearing a soft knee's clothes, and clipping is far worse for ASR
 *    than quiet audio. A compressor limits over TIME (attack/release) instead of
 *    reshaping each sample, so peaks are ridden down rather than squared off.
 *
 * A limiter is not AGC: it only pulls DOWN peaks above the threshold and never
 * lifts a quiet speaker toward a loud one, so the per-speaker level differences
 * diarization separates voices by are still intact in the normal range.
 *
 * @author DES
 * @date 02-08-2026
 */

import { isSafariWebKit } from "./safariDetect";

/**
 * Makeup gain (~+6 dB) — recovers most of what losing AGC costs a distant talker
 * while leaving a close talker's peaks mostly under the limiter.
 *
 * ⚠️ Unverified: nobody has measured browser mic levels for this room. If field
 * recordings show distant speakers still too quiet, the fix is an opt-in
 * room/table mode carrying a higher gain — the way native scopes its 2.5x — NOT a
 * bigger constant here, which would drag every close-talker recording into the
 * limiter.
 */
export const CAPTURE_GAIN = 2.0;

/** Peak limiter — transparent until the signal is near full scale. */
const LIMITER = {
  /** dBFS; ~0.7 linear, so normal speech never touches it. */
  threshold: -3,
  /** dB of soft knee around the threshold. */
  knee: 6,
  /** High enough to act as a limiter rather than a compressor. */
  ratio: 20,
  /** Fast enough to catch a consonant transient. */
  attack: 0.003,
  /** Slow enough not to pump on syllables. */
  release: 0.25,
} as const;

export type GainStage = {
  /** Processed stream — feed this to MediaRecorder and to the meter. */
  stream: MediaStream;
  /** Gain actually applied (1 when bypassed), so the meter can scale its ceiling. */
  gain: number;
  stop: () => void;
};

/**
 * Wrap `source` in gain + limiting and return the processed stream.
 *
 * Returns `null` when the stage cannot be applied — the caller then records the
 * raw stream unchanged. Bypassed deliberately on Safari/WebKit: routing
 * MediaRecorder through a `MediaStreamAudioDestinationNode` has a history of
 * producing empty or silent files there, and a silent recording loses the whole
 * meeting while a quiet one merely makes it harder to hear. Not a trade worth
 * making without a real Safari device to test on.
 */
export function createGainStage(
  source: MediaStream,
  gain: number = CAPTURE_GAIN,
  /**
   * Call audio from the desktop app (`systemAudio.ts`), mixed in at unity: it
   * is already a clean digital signal, so the mic's makeup gain would only push
   * it into the limiter. Both share the limiter, which catches the sum when
   * someone in the room talks over the call. (30-09-2026)
   */
  systemAudio?: MediaStream | null,
): GainStage | null {
  if (isSafariWebKit()) return null;

  const AudioCtx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  // With call audio there is always something to mix, so gain 1 is no bypass.
  if (!AudioCtx || (gain === 1 && !systemAudio)) return null;

  let ctx: AudioContext;
  try {
    ctx = new AudioCtx();
  } catch {
    return null;
  }

  try {
    const input = ctx.createMediaStreamSource(source);
    const gainNode = ctx.createGain();
    gainNode.gain.value = gain;

    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = LIMITER.threshold;
    limiter.knee.value = LIMITER.knee;
    limiter.ratio.value = LIMITER.ratio;
    limiter.attack.value = LIMITER.attack;
    limiter.release.value = LIMITER.release;

    const destination = ctx.createMediaStreamDestination();
    input.connect(gainNode);
    gainNode.connect(limiter);
    limiter.connect(destination);

    const callInput = systemAudio ? ctx.createMediaStreamSource(systemAudio) : null;
    callInput?.connect(limiter);

    // Autoplay policy can leave the context suspended until a gesture; recording
    // starts from a click, but resume defensively so the graph is never stalled.
    void ctx.resume().catch(() => undefined);

    return {
      stream: destination.stream,
      gain,
      stop: () => {
        try {
          input.disconnect();
          callInput?.disconnect();
          gainNode.disconnect();
          limiter.disconnect();
        } catch {
          /* already torn down */
        }
        destination.stream.getTracks().forEach((t) => t.stop());
        void ctx.close().catch(() => undefined);
      },
    };
  } catch {
    void ctx.close().catch(() => undefined);
    return null;
  }
}
